import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
  Paper,
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useSongs } from '../../hooks/useSongs';
import { useImportQuizQuestions } from '../../hooks/useQuizQuestions';

// Import new components
import QuestionEditModal from './QuestionEditModal';
import QuestionViewModal from './QuestionViewModal';
import DuplicateManagement from './DuplicateManagement';
import ReviewTable from './ReviewTable';

// Import utilities
import { validateQuestions, sanitizeQuestionData } from './questionValidation';
import { removeDuplicatesByIndices } from './duplicateDetection';
import Header from '../Header';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '12px',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: '#ccc',
  },
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': {
      borderColor: '#333',
    },
    '&:hover fieldset': {
      borderColor: '#4CAF50',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4CAF50',
    },
  },
  '& .MuiSelect-icon': {
    color: '#ccc',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#4CAF50',
  color: '#fff',
  padding: '12px 24px',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: '#45a049',
  },
  '&:disabled': {
    backgroundColor: '#333',
    color: '#666',
  },
}));

const UploadArea = styled(Box)(({ theme, isDragOver }) => ({
  border: `2px dashed ${isDragOver ? '#4CAF50' : '#333'}`,
  borderRadius: '12px',
  padding: '40px 20px',
  textAlign: 'center',
  backgroundColor: isDragOver ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.05)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#4CAF50',
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
  },
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  '& .MuiStepLabel-root': {
    color: '#ccc',
  },
  '& .MuiStepIcon-root': {
    color: '#333',
    '&.Mui-active': {
      color: '#4CAF50',
    },
    '&.Mui-completed': {
      color: '#4CAF50',
    },
  },
}));

const CsvQuizQuestionImport = ({ onBack }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: songs = [], isLoading: songsLoading } = useSongs();
  const importMutation = useImportQuizQuestions();

  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    questionBank: '',
    song: null,
    questionType: '',
    answerType: '',
    mood: '',
    topic: '',
    difficulty: '',
    basePoints: 10
  });
  const [lookupData, setLookupData] = useState({
    questionBanks: [],
    questionTypes: [],
    answerTypes: [],
    questionMoods: [],
    questionTopics: [],
    difficulties: []
  });
  const [dataLoading, setDataLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState({});
  const [lookupErrors, setLookupErrors] = useState([]);

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);

  const steps = ['Configure Settings', 'Upload CSV File', 'Review & Import'];

  // Enhanced lookup service with retry mechanism
  const lookupService = {
    fetchItems: async (type, retries = 2) => {
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/lookups/${type}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const data = await response.json();

          // Validate that we received an array
          if (!Array.isArray(data)) {
            throw new Error(`Invalid data format received for ${type}`);
          }

          return data;
        } catch (error) {
          console.error(`Attempt ${attempt + 1} failed for ${type}:`, error);

          if (attempt === retries) {
            // Last attempt failed, throw the error
            throw new Error(`Failed to fetch ${type} after ${retries + 1} attempts: ${error.message}`);
          }

          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }
  };

  // Load lookup data with improved error handling
  const loadAllData = useCallback(async () => {
    setDataLoading(true);
    try {
      // Fetch all lookup data in parallel with individual error handling
      const lookupPromises = [
        { type: 'question-bank', promise: lookupService.fetchItems('question-bank') },
        { type: 'difficulty', promise: lookupService.fetchItems('difficulty') },
        { type: 'question-type', promise: lookupService.fetchItems('question-type') },
        { type: 'answer-type', promise: lookupService.fetchItems('answer-type') },
        { type: 'question-mood', promise: lookupService.fetchItems('question-mood') },
        { type: 'question-topic', promise: lookupService.fetchItems('question-topic') }
      ];

      const results = await Promise.allSettled(lookupPromises.map(item => item.promise));

      // Process results and handle individual failures
      const lookupResults = {};
      const failedLookups = [];

      results.forEach((result, index) => {
        const lookupType = lookupPromises[index].type;
        if (result.status === 'fulfilled') {
          // Filter only active items
          const activeItems = result.value.filter(item => item.status === 'active' || item.status === 1);
          lookupResults[lookupType.replace('-', '')] = activeItems;
        } else {
          console.error(`Failed to fetch ${lookupType}:`, result.reason);
          failedLookups.push({
            type: lookupType,
            error: result.reason?.message || 'Unknown error'
          });
          lookupResults[lookupType.replace('-', '')] = [];
        }
      });

      // Update lookup errors state
      setLookupErrors(failedLookups);

      // Set the lookup data
      setLookupData({
        questionBanks: lookupResults.questionbank || [],
        difficulties: lookupResults.difficulty || [],
        questionTypes: lookupResults.questiontype || [],
        answerTypes: lookupResults.answertype || [],
        questionMoods: lookupResults.questionmood || [],
        questionTopics: lookupResults.questiontopic || []
      });

      // Set default values only if not already set
      if (!formData.questionBank && lookupResults.questionbank?.length > 0) {
        const defaultQuestionBank = lookupResults.questionbank.find(qb =>
          qb.bank_name?.toLowerCase() === 'songq'
        ) || lookupResults.questionbank[0];
        setFormData(prev => ({
          ...prev,
          questionBank: defaultQuestionBank.bank_id
        }));
      }

      if (!formData.questionType && lookupResults.questiontype?.length > 0) {
        const defaultQuestionType = lookupResults.questiontype.find(qt =>
          qt.type_name === 'text'
        ) || lookupResults.questiontype[0];
        setFormData(prev => ({
          ...prev,
          questionType: defaultQuestionType.type_name
        }));
      }

      if (!formData.answerType && lookupResults.answertype?.length > 0) {
        const defaultAnswerType = lookupResults.answertype.find(at =>
          at.type_name === 'single_choice'
        ) || lookupResults.answertype[0];
        setFormData(prev => ({
          ...prev,
          answerType: defaultAnswerType.type_name
        }));
      }

      if (failedLookups.length > 0) {
        enqueueSnackbar(
          `Failed to load ${failedLookups.length} lookup type(s). Some form fields may be empty.`,
          {
            variant: 'warning',
            autoHideDuration: 8000
          }
        );
      }

    } catch (error) {
      console.error('Error loading lookup data:', error);
      enqueueSnackbar('Error loading form data. Please refresh the page.', { variant: 'error' });

      // Set empty arrays to prevent crashes
      setLookupData({
        questionBanks: [],
        difficulties: [],
        questionTypes: [],
        answerTypes: [],
        questionMoods: [],
        questionTopics: []
      });
    } finally {
      setDataLoading(false);
    }
  }, [enqueueSnackbar]);

  // Enhanced form validation
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // Check if lookup data is available
      if (lookupData.questionBanks.length === 0) {
        newErrors.questionBank = 'Question Bank data not available. Please retry loading.';
      } else if (!formData.questionBank) {
        newErrors.questionBank = 'Question Bank is required';
      }

      if (!formData.song) newErrors.song = 'Song is required';

      if (lookupData.questionTypes.length === 0) {
        newErrors.questionType = 'Question Type data not available. Please retry loading.';
      } else if (!formData.questionType) {
        newErrors.questionType = 'Question Type is required';
      }

      if (lookupData.answerTypes.length === 0) {
        newErrors.answerType = 'Answer Type data not available. Please retry loading.';
      } else if (!formData.answerType) {
        newErrors.answerType = 'Answer Type is required';
      }

      if (lookupData.questionMoods.length === 0) {
        newErrors.mood = 'Mood data not available. Please retry loading.';
      } else if (!formData.mood) {
        newErrors.mood = 'Mood is required';
      }

      if (lookupData.questionTopics.length === 0) {
        newErrors.topic = 'Topic data not available. Please retry loading.';
      } else if (!formData.topic) {
        newErrors.topic = 'Topic is required';
      }

      if (lookupData.difficulties.length === 0) {
        newErrors.difficulty = 'Difficulty data not available. Please retry loading.';
      } else if (!formData.difficulty) {
        newErrors.difficulty = 'Difficulty is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation handlers
  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Modal handlers
  const handleEditQuestion = (question, index) => {
    setSelectedQuestion(question);
    setSelectedQuestionIndex(index);
    setEditModalOpen(true);
  };

  const handleViewQuestion = (question, index) => {
    setSelectedQuestion(question);
    setSelectedQuestionIndex(index);
    setViewModalOpen(true);
  };

  const handleDeleteQuestion = (index) => {
    const updatedQuestions = parsedData.filter((_, i) => i !== index);
    const updatedValidation = validationResults.filter((_, i) => i !== index);
    setParsedData(updatedQuestions);
    setValidationResults(updatedValidation);
    enqueueSnackbar('Question deleted successfully', { variant: 'success' });
  };

  const handleSaveQuestion = (updatedQuestion) => {
    const updatedQuestions = [...parsedData];
    updatedQuestions[selectedQuestionIndex] = updatedQuestion;
    setParsedData(updatedQuestions);

    // Re-validate the updated question
    const updatedValidation = [...validationResults];
    updatedValidation[selectedQuestionIndex] = validateQuestions([updatedQuestion], lookupData)[0];
    setValidationResults(updatedValidation);

    enqueueSnackbar('Question updated successfully', { variant: 'success' });
  };

  const handleOpenDuplicateManager = () => {
    setDuplicateModalOpen(true);
  };

  const handleRemoveDuplicates = (indicesToRemove) => {
    const updatedQuestions = removeDuplicatesByIndices(parsedData, indicesToRemove);
    const updatedValidation = removeDuplicatesByIndices(validationResults, indicesToRemove);
    setParsedData(updatedQuestions);
    setValidationResults(updatedValidation);
    enqueueSnackbar(`Removed ${indicesToRemove.length} duplicate questions`, { variant: 'success' });
  };

  // Form field handlers
  const updateFormField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Song search functionality
  const filteredSongs = useMemo(() => {
    // return songs.sort((a, b) => {
    //   const aName = (a.name || '').toLowerCase();
    //   const bName = (b.name || '').toLowerCase();
    //   return aName.localeCompare(bName);
    // });
    return songs;
  }, [songs]);

  // CSV file handling
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      enqueueSnackbar('Please select a valid CSV file (.csv)', { variant: 'error' });
      return;
    }

    setUploadedFile(file);
    processFile(file);
  };

  const processFile = async (file) => {
    setIsProcessing(true);
    try {
      const parsedQuestions = await parseCSVContent(file);
      setParsedData(parsedQuestions);

      // Validate each question using enhanced validation
      const validationResults = validateQuestions(parsedQuestions, lookupData);
      setValidationResults(validationResults);

    } catch (error) {
      console.error('File processing error:', error);
      enqueueSnackbar(`Error processing file: ${error.message}`, { variant: 'error' });
      setParsedData([]);
      setValidationResults([]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Parse CSV content
  const parseCSVContent = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const lines = content.split('\n').filter(line => line.trim());

          if (lines.length < 2) {
            reject(new Error('CSV file must have at least a header row and one data row'));
            return;
          }

          // Parse header
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

          // Parse data rows
          const questions = [];
          for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            if (values.length === 0) continue;

            const rowData = {};
            headers.forEach((header, index) => {
              rowData[header] = values[index] || '';
            });

            // Extract answers (up to 4) with enhanced error handling
            const answers = [];
            for (let j = 1; j <= 4; j++) {
              try {
                const answerText = rowData[`Answer ${j}`] || rowData[`answer_${j}`] || rowData[`Answer${j}`];
                const isCorrect = rowData[`Answer ${j} Correct`] || rowData[`answer_${j}_correct`] || rowData[`Answer${j}Correct`];
                const answerReason = rowData[`Answer ${j} Reason`] || rowData[`answer_${j}_reason`] || rowData[`Answer${j}Reason`];

                // Safely convert answer text to string
                const safeAnswerText = answerText ? String(answerText).trim() : '';

                if (safeAnswerText) {
                  // Safely convert correct flag to boolean
                  const safeIsCorrect = isCorrect === 'TRUE' || isCorrect === 'true' || isCorrect === '1' || isCorrect === 'Yes' || isCorrect === 'yes' || isCorrect === true;

                  // Safely convert reason to string
                  const safeAnswerReason = answerReason ? String(answerReason).trim() : '';

                  answers.push({
                    text: safeAnswerText,
                    correct: safeIsCorrect,
                    reason: safeAnswerReason
                  });
                }
              } catch (answerError) {
                console.error(`Error processing answer ${j} for row ${i + 1}:`, answerError);
                // Continue processing other answers
              }
            }

            // Create raw question object
            const rawQuestion = {
              rowNumber: i + 1,
              questionText: rowData['Question Text'] || rowData['question_text'] || rowData['QuestionText'] || '',
              timeLimit: rowData['Time Limit'] || rowData['time_limit'] || rowData['TimeLimit'] || 30,
              questionClue: rowData['Question Clue'] || rowData['question_clue'] || rowData['QuestionClue'] || '',
              answers: answers,
              // Apply pre-selected values (ensure they are the correct types)
              questionType: formData.questionType || '',
              answerType: formData.answerType || '',
              difficulty: formData.difficulty || 0,
              questionMood: formData.mood || 0,
              questionTopic: formData.topic || 0,
              questionBank: formData.questionBank || 0,
              songId: formData.song?.songs_master_id || 0
            };

            // Sanitize the question data to ensure proper types
            try {
              const sanitizedQuestion = sanitizeQuestionData(rawQuestion);
              questions.push(sanitizedQuestion);
            } catch (sanitizeError) {
              console.error(`Error sanitizing question at row ${i + 1}:`, sanitizeError);
              // Still add the raw question but mark it for validation errors
              questions.push({
                ...rawQuestion,
                sanitizationError: sanitizeError.message
              });
            }
          }

          resolve(questions);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  };

  // Parse a single CSV line handling quoted values
  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    values.push(current.trim());
    return values;
  };

  // Download CSV template
  const downloadTemplate = () => {
    const headers = [
      'Question Text', 'Time Limit', 'Question Clue',
      'Answer 1', 'Answer 1 Correct', 'Answer 1 Reason',
      'Answer 2', 'Answer 2 Correct', 'Answer 2 Reason',
      'Answer 3', 'Answer 3 Correct', 'Answer 3 Reason',
      'Answer 4', 'Answer 4 Correct', 'Answer 4 Reason'
    ];

    const sampleData = [
      'What is the capital of France?', '30', 'Think about European capitals',
      'Paris', 'TRUE', 'Paris is the capital and largest city of France',
      'London', 'FALSE', 'London is the capital of the United Kingdom',
      'Berlin', 'FALSE', 'Berlin is the capital of Germany',
      'Madrid', 'FALSE', 'Madrid is the capital of Spain'
    ];

    const csvContent = [
      headers.join(','),
      sampleData.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'quiz_questions_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Enhanced import validation
  const validateForImport = () => {
    const errors = [];

    // Check if there are any questions
    if (parsedData.length === 0) {
      errors.push('No questions to import');
      return { isValid: false, errors };
    }

    // Check if all questions pass validation
    const invalidQuestions = validationResults.filter(result => !result.isValid);
    if (invalidQuestions.length > 0) {
      errors.push(`${invalidQuestions.length} questions have validation errors that must be fixed before import`);
    }

    // Check if required form data is present
    if (!formData.song?.songs_master_id) {
      errors.push('Song selection is required');
    }

    if (!formData.questionBank) {
      errors.push('Question bank selection is required');
    }

    if (!formData.questionType) {
      errors.push('Question type selection is required');
    }

    if (!formData.answerType) {
      errors.push('Answer type selection is required');
    }

    if (!formData.difficulty) {
      errors.push('Difficulty selection is required');
    }

    if (!formData.mood) {
      errors.push('Mood selection is required');
    }

    if (!formData.topic) {
      errors.push('Topic selection is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Enhanced import functionality with strict validation
  const handleImport = async () => {
    try {
      // Pre-import validation
      const importValidation = validateForImport();
      if (!importValidation.isValid) {
        enqueueSnackbar(
          `Cannot import: ${importValidation.errors.join(', ')}`,
          { variant: 'error', autoHideDuration: 8000 }
        );
        return;
      }

      // Helper function to get lookup name by ID or existing name
      const getLookupName = (lookupArray, value, lookupType) => {
        if (!lookupArray || !value) return '';

        // Define the correct ID and name fields for each lookup type
        const fieldMappings = {
          questionBanks: { idField: 'bank_id', nameField: 'bank_name' },
          difficulties: { idField: 'difficulty_id', nameField: 'level_name' },
          questionMoods: { idField: 'mood_type_id', nameField: 'mood_name' },
          questionTopics: { idField: 'topic_type_id', nameField: 'topic_name' },
          questionTypes: { idField: 'question_type_id', nameField: 'type_name' },
          answerTypes: { idField: 'answer_type_id', nameField: 'type_name' }
        };

        const mapping = fieldMappings[lookupType];
        if (!mapping) return '';

        // If value is already a string (like 'text'), return it directly
        if (typeof value === 'string' && isNaN(value)) {
          return value;
        }

        // Otherwise, look up by ID
        const item = lookupArray.find(item => item[mapping.idField] === value);
        return item ? item[mapping.nameField] : '';
      };

      // Prepare questions for import with proper field mapping for server
      const questionsToImport = parsedData.map((question, index) => {
        try {
          // Get the sanitized question from validation results
          const validationResult = validationResults[index];
          const sanitizedQuestion = validationResult?.sanitizedQuestion || sanitizeQuestionData(question);

          // For server: Convert IDs to names (server expects string names in CSV format)
          const questionBankName = getLookupName(lookupData.questionBanks, formData.questionBank, 'questionBanks') || 'Songq';
          const difficultyName = getLookupName(lookupData.difficulties, formData.difficulty, 'difficulties');
          const moodName = getLookupName(lookupData.questionMoods, formData.mood, 'questionMoods');
          const topicName = getLookupName(lookupData.questionTopics, formData.topic, 'questionTopics');
          const questionTypeName = getLookupName(lookupData.questionTypes, formData.questionType, 'questionTypes');
          const answerTypeName = getLookupName(lookupData.answerTypes, formData.answerType, 'answerTypes');

          // Debug logging
          // console.log('Lookup conversion debug:', {
          //   formData: {
          //     questionBank: formData.questionBank,
          //     difficulty: formData.difficulty,
          //     mood: formData.mood,
          //     topic: formData.topic,
          //     questionType: formData.questionType,
          //     answerType: formData.answerType
          //   },
          //   convertedNames: {
          //     questionBankName,
          //     difficultyName,
          //     moodName,
          //     topicName,
          //     questionTypeName,
          //     answerTypeName
          //   }
          // });

          // Prepare data in TWO formats:
          // 1. For server (string names for CSV processing)
          const serverData = {
            questionText: sanitizedQuestion.questionText,
            timeLimit: sanitizedQuestion.timeLimit,
            questionClue: sanitizedQuestion.questionClue || '',
            answers: sanitizedQuestion.answers,
            // Server expects string names
            questionType: questionTypeName,
            answerType: answerTypeName,
            difficulty: difficultyName,
            questionMood: moodName,
            questionTopic: topicName,
            questionBank: questionBankName,
            songId: formData.song.songs_master_id
          };

          // 2. For validation (mixed types as expected by validation function)
          const validationData = {
            questionText: sanitizedQuestion.questionText,
            timeLimit: sanitizedQuestion.timeLimit,
            questionClue: sanitizedQuestion.questionClue || '',
            answers: sanitizedQuestion.answers,
            // Validation expects: string names for types, numeric IDs for others
            questionType: questionTypeName,
            answerType: answerTypeName,
            difficulty: formData.difficulty,        // Keep as numeric ID
            questionMood: formData.mood,           // Keep as numeric ID
            questionTopic: formData.topic,         // Keep as numeric ID
            questionBank: formData.questionBank,   // Keep as numeric ID
            songId: formData.song.songs_master_id
          };

          // Store both formats in the object
          serverData._validationData = validationData;
          return serverData;
        } catch (error) {
          console.error(`Error preparing question ${index + 1} for import:`, error);
          throw new Error(`Question ${index + 1} preparation failed: ${error.message}`);
        }
      });

      // Final validation of prepared questions using validation data format
      // console.log('Questions prepared for final validation:', questionsToImport);
      const questionsForValidation = questionsToImport.map(q => q._validationData || q);
      const finalValidation = validateQuestions(questionsForValidation, lookupData);
      const finalInvalidQuestions = finalValidation.filter(result => !result.isValid);

      if (finalInvalidQuestions.length > 0) {
        console.error('Final validation failed:', finalInvalidQuestions);
        console.error('Invalid questions details:', finalInvalidQuestions.map(q => ({
          errors: q.errors,
          question: q.sanitizedQuestion || 'No sanitized question'
        })));
        enqueueSnackbar(
          `Final validation failed for ${finalInvalidQuestions.length} questions. Check console for details.`,
          { variant: 'error' }
        );
        return;
      }

      // Clean up server data (remove validation data before sending)
      const cleanedQuestionsForServer = questionsToImport.map(q => {
        const { _validationData, ...serverData } = q;
        return serverData;
      });

      // Prepare the data for import using the existing format
      const processedImportData = {
        processedQuestions: cleanedQuestionsForServer,
        songs_master_ids: [formData.song.songs_master_id],
        isProcessedData: true
      };

      // console.log('Sending import data to server:', processedImportData);
      const result = await importMutation.mutateAsync(processedImportData);

      // Handle import result with better error reporting
      if (result?.errors && result.errors.length > 0) {
        console.error('Import completed with errors:', result);
        enqueueSnackbar(
          `Import completed with ${result.errors.length} errors. Check console for details.`,
          { variant: 'warning', autoHideDuration: 10000 }
        );

        // Show specific errors if available
        result.errors.forEach((error, index) => {
          console.error(`Import Error ${index + 1}:`, error);
        });
      } else {
        enqueueSnackbar(
          `Successfully imported ${result.summary?.inserted || parsedData.length} questions!`,
          { variant: 'success' }
        );
      }

      // Reset form and go back
      setActiveStep(0);
      setUploadedFile(null);
      setParsedData([]);
      setValidationResults([]);
      onBack();

    } catch (error) {
      console.error('Import error:', error);
      enqueueSnackbar(`Import failed: ${error.message}`, { variant: 'error' });
    }
  };


  useEffect(() => {
    loadAllData();
  }, [loadAllData]);


  return (
    <>
      <Header />
      <Box sx={{
        maxWidth: 1200,
        margin: '0 auto',
        px: 3,
        py: 4,
        minHeight: '100vh',
        backgroundColor: '#121212'
      }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ color: '#4CAF50' }}
        >
          Back to Import Center
        </Button> */}
          <Typography
            variant="h4"
            sx={{
              color: '#fff',
              fontWeight: 600,
              flex: 1
            }}
          >
            CSV Quiz Question Import
          </Typography>
        </Box>

        <StyledCard>
          <CardContent sx={{ p: 4 }}>
            {/* Stepper */}
            <StyledStepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </StyledStepper>

            {/* Step Content */}
            {activeStep === 0 && (
              <Box>
                <Typography variant="h6" sx={{ color: '#fff', mb: 3 }}>
                  Configure Question Settings
                </Typography>
                <Alert severity="info" sx={{ mb: 3, backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
                  These settings will be applied to all questions imported from your CSV file.
                </Alert>

                {/* Lookup Data Status */}
                {!dataLoading && lookupErrors.length === 0 && (
                  <Alert severity="success" sx={{ mb: 3, backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
                    All form data loaded successfully. You can now configure your question settings.
                  </Alert>
                )}

                {dataLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: '#4CAF50', mb: 2 }} />
                    <Typography sx={{ color: '#ccc' }}>Loading form data...</Typography>
                  </Box>
                ) : lookupErrors.length > 0 ? (
                  <Box sx={{ mb: 3 }}>
                    <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
                      Failed to load some form data. Please check your connection and try again.
                    </Alert>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                      {lookupErrors.map((error, index) => (
                        <Typography key={index} variant="body2" sx={{ color: '#f44336' }}>
                          • {error.type}: {error.error}
                        </Typography>
                      ))}
                    </Box>
                    <Button
                      variant="outlined"
                      onClick={loadAllData}
                      sx={{ color: '#4CAF50', borderColor: '#4CAF50' }}
                    >
                      Retry Loading Data
                    </Button>
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    {/* Question Bank Selection */}
                    <Grid item xs={12} md={6}>
                      <StyledFormControl fullWidth error={!!errors.questionBank} required>
                        <InputLabel>Question Bank</InputLabel>
                        <Select
                          value={formData.questionBank}
                          onChange={(e) => updateFormField('questionBank', e.target.value)}
                          label="Question Bank"
                        >
                          <MenuItem value="">
                            <em>Select question bank...</em>
                          </MenuItem>
                          {lookupData.questionBanks.map((bank) => (
                            <MenuItem key={bank.bank_id} value={bank.bank_id}>
                              <Typography sx={{ color: '#fff' }}>{bank.bank_name}</Typography>
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.questionBank && (
                          <FormHelperText>{errors.questionBank}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>

                    {/* Song Selection */}
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        options={filteredSongs}
                        getOptionLabel={(song) => song.name || ""}
                        value={formData.song}
                        onChange={(_, newValue) => updateFormField('song', newValue)}
                        loading={songsLoading}
                        loadingText="Loading songs..."
                        noOptionsText="No songs found"
                        clearOnBlur={false}
                        selectOnFocus
                        handleHomeEndKeys
                        freeSolo={false}
                        autoHighlight
                        openOnFocus
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Song *"
                            placeholder="Search by song name..."
                            error={!!errors.song}
                            helperText={errors.song}
                            sx={{
                              '& .MuiInputLabel-root': { color: '#ccc' },
                              '& .MuiOutlinedInput-root': {
                                color: '#fff',
                                '& fieldset': { borderColor: '#333' },
                                '&:hover fieldset': { borderColor: '#4CAF50' },
                                '&.Mui-focused fieldset': { borderColor: '#4CAF50' },
                              },
                            }}
                          />
                        )}
                        renderOption={(props, song) => (
                          <Box component="li" {...props} sx={{ color: '#fff' }}>
                            <Box>
                              <Typography variant="body1">{song.name}</Typography>
                              <Typography variant="caption" sx={{ color: '#ccc' }}>
                                {song.artist} • {song.duration}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      />
                    </Grid>

                    {/* Question Type */}
                    <Grid item xs={12} md={6}>
                      <StyledFormControl fullWidth error={!!errors.questionType} required>
                        <InputLabel>Question Type</InputLabel>
                        <Select
                          value={formData.questionType}
                          onChange={(e) => updateFormField('questionType', e.target.value)}
                          label="Question Type"
                        >
                          <MenuItem value="">
                            <em>Select question type...</em>
                          </MenuItem>
                          {lookupData.questionTypes.map((type) => (
                            <MenuItem key={type.question_type_id} value={type.type_name}>
                              <Box>
                                <Typography variant="body1" sx={{ color: '#fff' }}>
                                  {type.display_name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#ccc' }}>
                                  {type.type_name}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.questionType && (
                          <FormHelperText>{errors.questionType}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>

                    {/* Answer Type */}
                    <Grid item xs={12} md={6}>
                      <StyledFormControl fullWidth error={!!errors.answerType} required>
                        <InputLabel>Answer Type</InputLabel>
                        <Select
                          value={formData.answerType}
                          onChange={(e) => updateFormField('answerType', e.target.value)}
                          label="Answer Type"
                        >
                          <MenuItem value="">
                            <em>Select answer type...</em>
                          </MenuItem>
                          {lookupData.answerTypes.map((type) => (
                            <MenuItem key={type.answer_type_id} value={type.type_name}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1" sx={{ color: '#fff' }}>
                                  {type.display_name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#ccc' }}>
                                  ({type.type_name})
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.answerType && (
                          <FormHelperText>{errors.answerType}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>

                    {/* Question Mood */}
                    <Grid item xs={12} md={6}>
                      <StyledFormControl fullWidth error={!!errors.mood} required>
                        <InputLabel>Question Mood</InputLabel>
                        <Select
                          value={formData.mood}
                          onChange={(e) => updateFormField('mood', e.target.value)}
                          label="Question Mood"
                        >
                          <MenuItem value="">
                            <em>Select mood...</em>
                          </MenuItem>
                          {lookupData.questionMoods.map((mood) => (
                            <MenuItem key={mood.mood_type_id} value={mood.mood_type_id}>
                              <Typography sx={{ color: '#fff' }}>{mood.display_name}</Typography>
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.mood && (
                          <FormHelperText>{errors.mood}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>

                    {/* Question Topic */}
                    <Grid item xs={12} md={6}>
                      <StyledFormControl fullWidth error={!!errors.topic} required>
                        <InputLabel>Question Topic</InputLabel>
                        <Select
                          value={formData.topic}
                          onChange={(e) => updateFormField('topic', e.target.value)}
                          label="Question Topic"
                        >
                          <MenuItem value="">
                            <em>Select topic...</em>
                          </MenuItem>
                          {lookupData.questionTopics.map((topic) => (
                            <MenuItem key={topic.topic_type_id} value={topic.topic_type_id}>
                              <Typography sx={{ color: '#fff' }}>{topic.display_name}</Typography>
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.topic && (
                          <FormHelperText>{errors.topic}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>

                    {/* Difficulty Level */}
                    <Grid item xs={12} md={6}>
                      <StyledFormControl fullWidth error={!!errors.difficulty} required>
                        <InputLabel>Difficulty Level</InputLabel>
                        <Select
                          value={formData.difficulty}
                          onChange={(e) => updateFormField('difficulty', e.target.value)}
                          label="Difficulty Level"
                        >
                          <MenuItem value="">
                            <em>Select difficulty...</em>
                          </MenuItem>
                          {lookupData.difficulties.map((difficulty) => (
                            <MenuItem key={difficulty.difficulty_id} value={difficulty.difficulty_id}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography sx={{ color: '#fff' }}>{difficulty.level_name}</Typography>
                                {difficulty.bonus_points && (
                                  <Chip
                                    label={`+${difficulty.bonus_points}pts`}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#4CAF50',
                                      color: '#fff',
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.difficulty && (
                          <FormHelperText>{errors.difficulty}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>

                    {/* Base Points */}
                    {/* <Grid item xs={12} md={6}>
                      <StyledTextField
                        fullWidth
                        label="Base Points"
                        type="number"
                        value={formData.basePoints}
                        onChange={(e) => updateFormField('basePoints', parseInt(e.target.value) || 10)}
                        error={!!errors.basePoints}
                        helperText={errors.basePoints}
                        inputProps={{ min: 1, max: 100 }}
                      />
                    </Grid> */}
                  </Grid>
                )}
              </Box>
            )}

            {/* Step 2: CSV Upload */}
            {activeStep === 1 && (
              <Box>
                <Typography variant="h6" sx={{ color: '#fff', mb: 3 }}>
                  Upload CSV File
                </Typography>
                <Alert severity="info" sx={{ mb: 3, backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
                  Upload a CSV file containing your quiz questions. The file should include columns for Question Text, Answer 1, Answer 2, etc.
                </Alert>

                {/* Download Template Button */}
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadTemplate()}
                    sx={{ color: '#4CAF50', borderColor: '#4CAF50' }}
                  >
                    Download CSV Template
                  </Button>
                </Box>

                {/* File Upload Area */}
                <UploadArea
                  isDragOver={isDragOver}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('csv-file-input').click()}
                >
                  <input
                    id="csv-file-input"
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                  />

                  {isProcessing ? (
                    <Box>
                      <CircularProgress sx={{ color: '#4CAF50', mb: 2 }} />
                      <Typography sx={{ color: '#fff', mb: 1 }}>Processing file...</Typography>
                      <Typography sx={{ color: '#ccc', fontSize: '0.9em' }}>
                        Parsing {uploadedFile?.name} and validating question format
                      </Typography>
                    </Box>
                  ) : uploadedFile ? (
                    <Box>
                      <CheckIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 2 }} />
                      <Typography sx={{ color: '#fff', mb: 1 }}>
                        File uploaded successfully
                      </Typography>
                      <Typography sx={{ color: '#ccc' }}>
                        {uploadedFile.name} - {parsedData.length} questions found
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <UploadIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                      <Typography sx={{ color: '#fff', mb: 1, fontWeight: 500 }}>
                        {isDragOver ? 'Drop your CSV file here' : 'Drop your CSV file here or click to browse'}
                      </Typography>
                      <Typography sx={{ color: '#ccc', fontSize: '0.9em' }}>
                        Supported format: .csv files only
                      </Typography>
                    </Box>
                  )}
                </UploadArea>

                {/* File Processing Results */}
                {uploadedFile && parsedData.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="success" sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
                      Successfully parsed {parsedData.length} questions from {uploadedFile.name}
                    </Alert>
                  </Box>
                )}

                {/* Validation Errors */}
                {validationResults.some(result => !result.isValid) && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="warning" sx={{ backgroundColor: 'rgba(255, 152, 0, 0.1)' }}>
                      {validationResults.filter(result => !result.isValid).length} questions have validation errors.
                      You can review and fix them in the next step.
                    </Alert>
                  </Box>
                )}
              </Box>
            )}

            {/* Step 3: Review & Import */}
            {activeStep === 2 && (
              <Box>
                <Typography variant="h6" sx={{ color: '#fff', mb: 3 }}>
                  Review Questions
                </Typography>
                <Alert severity="info" sx={{ mb: 3, backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
                  Review the parsed questions below. You can edit or remove questions before importing.
                </Alert>

                {/* Summary */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', border: '1px solid #333' }}>
                      <Typography variant="h6" sx={{ color: '#4CAF50' }}>
                        {parsedData.length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#ccc' }}>
                        Total Questions
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', border: '1px solid #333' }}>
                      <Typography variant="h6" sx={{ color: '#4CAF50' }}>
                        {validationResults.filter(result => result.isValid).length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#ccc' }}>
                        Valid Questions
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', border: '1px solid #333' }}>
                      <Typography variant="h6" sx={{ color: '#ff9800' }}>
                        {validationResults.filter(result => !result.isValid).length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#ccc' }}>
                        Errors
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', border: '1px solid #333' }}>
                      <Typography variant="h6" sx={{ color: '#4CAF50' }}>
                        {formData.song?.name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#ccc' }}>
                        Target Song
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Enhanced Questions Review Table */}
                <ReviewTable
                  questions={parsedData}
                  validationResults={validationResults}
                  onEditQuestion={handleEditQuestion}
                  onDeleteQuestion={handleDeleteQuestion}
                  onViewQuestion={handleViewQuestion}
                  onOpenDuplicateManager={handleOpenDuplicateManager}
                  lookupData={lookupData}
                  songData={formData.song}
                />

                {parsedData.length > 10 && (
                  <Typography sx={{ color: '#ccc', mt: 2, textAlign: 'center' }}>
                    Showing first 10 of {parsedData.length} questions
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              {activeStep === steps.length - 1 && (
                <Box>
                  {(() => {
                    const invalidCount = validationResults.filter(result => !result.isValid).length;
                    const validCount = validationResults.filter(result => result.isValid).length;

                    return (
                      <>
                        {invalidCount > 0 && (
                          <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
                            <Typography variant="body2">
                              <strong>Cannot Import:</strong> {invalidCount} questions have validation errors that must be fixed first.
                              Only {validCount} questions are ready for import.
                            </Typography>
                          </Alert>
                        )}

                        {invalidCount === 0 && validCount > 0 && (
                          <Alert severity="success" sx={{ mb: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
                            <Typography variant="body2">
                              <strong>Ready to Import:</strong> All {validCount} questions have passed validation and are ready for import.
                            </Typography>
                          </Alert>
                        )}
                      </>
                    );
                  })()}
                </Box>
              )}
            </Box>
            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={activeStep === 0 ? onBack : handleBack}
                sx={{ color: '#ccc', borderColor: '#333' }}
              >
                {activeStep === 0 ? 'Cancel' : 'Back'}
              </Button>

              {activeStep < steps.length - 1 && (
                <StyledButton
                  variant="contained"
                  onClick={handleNext}
                  disabled={
                    dataLoading ||
                    (activeStep === 1 && (!uploadedFile || parsedData.length === 0))
                  }
                >
                  Next
                </StyledButton>
              )}

              {activeStep === steps.length - 1 && (
                <Box>
                  {(() => {
                    const invalidCount = validationResults.filter(result => !result.isValid).length;
                    const validCount = validationResults.filter(result => result.isValid).length;
                    const isDisabled = parsedData.length === 0 || invalidCount > 0 || importMutation.isLoading;

                    return (
                      <StyledButton
                        variant="contained"
                        onClick={handleImport}
                        disabled={isDisabled}
                        sx={{
                          backgroundColor: isDisabled ? '#333' : '#4CAF50',
                          '&:hover': {
                            backgroundColor: isDisabled ? '#333' : '#45a049'
                          }
                        }}
                      >
                        {importMutation.isLoading
                          ? 'Importing...'
                          : invalidCount > 0
                            ? `Fix ${invalidCount} Error${invalidCount > 1 ? 's' : ''} to Import`
                            : `Import ${validCount} Question${validCount > 1 ? 's' : ''}`
                        }
                      </StyledButton>
                    );
                  })()}
                </Box>
              )}
            </Box>
          </CardContent>
        </StyledCard>

        {/* Modals */}
        <QuestionEditModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          question={selectedQuestion}
          onSave={handleSaveQuestion}
          answerTypeConfig={{
            answerType: formData.answerType,
            questionType: formData.questionType
          }}
        />

        <QuestionViewModal
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          question={selectedQuestion}
          validationResult={selectedQuestionIndex !== null ? validationResults[selectedQuestionIndex] : null}
          lookupData={lookupData}
          songData={formData.song}
        />

        <DuplicateManagement
          open={duplicateModalOpen}
          onClose={() => setDuplicateModalOpen(false)}
          questions={parsedData}
          onRemoveDuplicates={handleRemoveDuplicates}
          similarityThreshold={0.8}
        />
      </Box>
    </>
  );
};

export default CsvQuizQuestionImport;
